import { ModelError, ModelValidationError } from '@api/tia/TiaFlowApi';

import { Alias, CompleteAlias } from '@api/tia/TiaAliasTableApi';
import {
  useAliasInputJsonSchema,
  useCreateAliasTable,
  useUpdateAliasTable,
  useDeleteAliasTable,
} from '@api/tia/TiaAliasTableApiHooks';
import { Alert } from '@stereograph/teia-system-design/components/Alert';
import { Key, useState } from 'react';
import { TableAliasEditor } from './AliasEditor';

export interface AliasEditorViewProps {
  key?: Key | null;
  inputAlias: CompleteAlias | Alias;
}

export const AliasEditorView = (props: AliasEditorViewProps) => {
  const { inputAlias, key } = props;

  const id = 'id' in inputAlias ? inputAlias.id : null;
  const alias = {
    ...inputAlias,
    id: undefined,
  };

  const { data: schema } = useAliasInputJsonSchema();
  const [textConfig, setTextConfig] = useState<string>(JSON.stringify(alias, null, 2));
  const [errors, setErrors] = useState<Array<ModelError>>([]);

  const {
    mutateAsync: createAliasTable,
    isPending: isCreating,
    isError: isCreatingError,
  } = useCreateAliasTable();

  const {
    mutateAsync: updateAliasTable,
    isPending: isUpdating,
    isError: isUpdatingError,
  } = useUpdateAliasTable();

  const {
    mutateAsync: deleteAliasTable,
    isPending: isDeleting,
    isError: isDeletingError,
  } = useDeleteAliasTable();

  const isLoading = isCreating || isUpdating || isDeleting;
  const isError = isCreatingError || isUpdatingError || isDeletingError;

  const onSuccess = () => {};

  const onError = (e: Error) => {
    const rep = e.cause as Response;
    rep.json().then((validationError: ModelValidationError) => {
      console.error(validationError);
      setErrors(validationError.detail);
    });
  };

  const onPost = () => {
    const config: Alias = JSON.parse(textConfig);
    if (id) {
      updateAliasTable({
        id: id,
        aliasTable: config,
      }, {
        onSuccess,
        onError,
      });
    } else {
      createAliasTable({
        aliasTable: config,
      }, {
        onSuccess,
        onError,
      });
    }
  };

  const onDelete = () => {
    if (id) {
      deleteAliasTable({
        id: id,
      }, {
        onSuccess,
        onError,
      });
    } else {
      console.error('Cannot delete: no id');
    }
  };

  return (
    <>
      {!!isError && (
        <Alert severity="error">
          Incorrect config
          <ul>
            {errors.map((e, idx) => (
              <li key={`error${idx}`}>{e.msg.replace('Input', e.loc.slice(2).join('.'))}</li>
            ))}
          </ul>
        </Alert>
      )}
      <TableAliasEditor
        sx={{
          flexGrow: 1,
          width: 1,
        }}
        key={key}
        schema={schema}
        aliasTableValue={textConfig}
        id={id}
        onChange={setTextConfig}
        onPost={onPost}
        onDelete={onDelete}
        isLoading={isLoading}
      />
    </>
  );
};
